/**
 * The registration window, on the two pages it governs.
 *
 * Admin sets an opening and a closing time in the console (Settings), and in
 * production mode both the sign-up page and the student sign-in page are shut
 * outside it: a countdown before, the form between, a closed notice after.
 *
 * Shared rather than written twice because the two pages must agree. They are
 * the same gate on the same window, and a sign-up page that had opened while
 * the sign-in page still showed a countdown would simply look broken.
 *
 * None of this decides anything. The server refuses the request
 * independently (`closedWindowRefusal` in server/src/routes/student.ts); this
 * is here so a student is told *why* and *when*, instead of meeting a refusal.
 */

import { useQuery } from '@tanstack/react-query'
import { CalendarClock, Lock } from 'lucide-react'
import { Link } from 'react-router-dom'
import { RULES, type RegistrationWindow } from '@sdg/shared'
import {
  Card,
  CardContent,
  CardDescription,
  CardEyebrow,
  CardHeader,
  CardTitle,
  Note,
} from './ui/card'
import { Button } from './ui/button'
import { get } from '../lib/api'
import {
  formatMoment,
  splitDuration,
  useWindowState,
  type GateStatus,
  type WindowState,
} from '../lib/countdown'

export type { GateStatus }

export interface Gate extends WindowState {
  window: RegistrationWindow | undefined
  refetch: () => void
}

/**
 * Reads the registration window and works out where we are in it, once a
 * second. The derivation itself lives in `useWindowState`, shared with the
 * booth scanner so there is one copy of the rule.
 */
export function useRegistrationGate(): Gate {
  const query = useQuery({
    queryKey: ['registration-window'],
    queryFn: () => get<RegistrationWindow>('/api/registration', 'none'),
    staleTime: 30_000,
  })

  return {
    ...useWindowState(query.data, query.isPending),
    window: query.data,
    refetch: () => void query.refetch(),
  }
}

/**
 * True for any of the refusals the server sends when a window is shut.
 *
 * Covers the booth codes as well as the student ones: each page only ever
 * meets its own, and one predicate means a new window cannot be added without
 * its refusal being recognised.
 */
export function isWindowRefusal(message: string): boolean {
  return (
    message === 'registration_not_open' ||
    message === 'registration_closed' ||
    message === 'booth_not_open' ||
    message === 'booth_closed'
  )
}

// ── The cards ─────────────────────────────────────────────────

/** Which page is asking, so the wording fits what the student came to do. */
export type GateAudience = 'register' | 'login'

const CARD = 'w-full max-w-md rounded-2xl border border-white bg-white/90 text-slate-900 shadow-xl shadow-blue-900/10'

function Shell({
  eyebrow,
  title,
  description,
  children,
}: {
  eyebrow: string
  title: string
  description: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <Card className={CARD}>
      <CardHeader className="space-y-1 px-6 pb-2 pt-6">
        <CardEyebrow className="text-[11px] font-bold uppercase tracking-widest text-orange-600">
          {eyebrow}
        </CardEyebrow>
        <CardTitle className="text-2xl font-bold text-[#0B1E3F]">{title}</CardTitle>
        <CardDescription className="text-sm text-slate-500">{description}</CardDescription>
      </CardHeader>
      <CardContent className="px-6 pb-6 pt-3">{children}</CardContent>
    </Card>
  )
}

/** Shown while the window is still being read. */
export function GateLoading() {
  return (
    <Card className={CARD}>
      <CardContent className="grid place-items-center gap-3 px-6 py-20">
        <span className="size-8 animate-spin rounded-full border-[3px] border-blue-100 border-t-orange-500" />
        <p className="text-sm text-slate-500">Just a moment…</p>
      </CardContent>
    </Card>
  )
}

/**
 * The countdown, before the window opens.
 *
 * Four tiles rather than a line of prose: someone arriving early wants to know
 * *how long*, and working that out from a date is work the page should do for
 * them. The seconds tile is what makes it visibly live — without it the card
 * reads as a static notice and people reload it.
 */
export function GateOpensSoon({
  opensAt,
  seconds,
  audience,
}: {
  opensAt: string | null
  seconds: number
  audience: GateAudience
}) {
  const parts = splitDuration(seconds)
  const tiles = [
    { label: parts.days === 1 ? 'Day' : 'Days', value: parts.days },
    { label: 'Hours', value: parts.hours },
    { label: 'Minutes', value: parts.minutes },
    { label: 'Seconds', value: parts.seconds },
  ]

  return (
    <Shell
      eyebrow={audience === 'register' ? 'Student Registration' : 'Student sign-in'}
      title={audience === 'register' ? 'Registration opens soon' : 'Sign-in opens soon'}
      description={
        audience === 'register'
          ? `Come back when the countdown ends to sign up, visit ${RULES.luckyDrawThreshold} booths for the lucky draw and ${RULES.certificateThreshold} for your e-certificate.`
          : 'Registration has not opened yet, so there are no passes to open. The countdown below is the same one on the sign-up page.'
      }
    >
      <div className="grid grid-cols-4 gap-2" role="timer" aria-live="off">
        {tiles.map((tile) => (
          <div
            key={tile.label}
            className="rounded-xl border border-blue-100 bg-blue-50/70 px-1 py-3 text-center"
          >
            <p className="text-2xl font-black leading-none tabular-nums text-[#0B1E3F]">
              {String(tile.value).padStart(2, '0')}
            </p>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
              {tile.label}
            </p>
          </div>
        ))}
      </div>

      {/*
        The exact moment as well as the countdown. The countdown answers "how
        long"; a student who wants to put it in their calendar needs the date,
        and `formatMoment` names the time zone so a student abroad is not left
        guessing whose 08:00 it is.
      */}
      <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-slate-200 bg-white px-3.5 py-3">
        <CalendarClock className="mt-0.5 size-4 shrink-0 text-orange-500" strokeWidth={2.2} />
        <p className="text-[13px] leading-snug text-slate-600">
          Registration opens <b className="font-semibold text-[#0B1E3F]">{formatMoment(opensAt)}</b>.
        </p>
      </div>

      {audience === 'login' && (
        <p className="mt-4 text-center text-sm text-slate-600">
          <Link to="/" className="font-semibold text-orange-600 hover:underline">
            Go to the registration page
          </Link>
        </p>
      )}
    </Shell>
  )
}

/**
 * Registration has closed — shown on the sign-up page only.
 *
 * There is no closed state on the sign-in page, which is the point of this
 * card: *new sign-ups* have stopped, but every student who did register still
 * needs their pass, so signing in keeps working. The whole job here is to send
 * them to it rather than leave them thinking the event has shut them out.
 */
export function GateClosed({ closedAt }: { closedAt: string | null }) {
  return (
    <Shell
      eyebrow="Student Registration"
      title="Registration has closed"
      description="New sign-ups have ended for this event."
    >
      <Note tone="error" className="rounded-lg border-red-200 bg-red-50/80 text-red-700">
        <span className="flex items-start gap-2.5">
          <Lock className="mt-0.5 size-4 shrink-0" strokeWidth={2.2} />
          <span>
            Registration closed on <b className="font-semibold">{formatMoment(closedAt)}</b>.
          </span>
        </span>
      </Note>

      <p className="mt-3.5 text-[13px] leading-relaxed text-slate-600">
        <b className="font-semibold text-[#0B1E3F]">Already registered?</b> You can still sign in.
        Your QR pass, your booth progress and your e-certificate all still work — sign in with the
        e-mail you registered with and we will send you a code.
      </p>

      <Link to="/login" className="mt-4 block">
        <Button
          block
          className="h-11 rounded-lg bg-orange-500 py-0 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange-600"
        >
          Sign in with your e-mail
        </Button>
      </Link>

      <p className="mt-3 text-center text-[12.5px] text-slate-500">
        Did not register in time? The help desk at the venue can advise you.
      </p>
    </Shell>
  )
}
